/**
 * 接口层。
 *
 * 原站实际契约（从 bundle 中还原）：
 *   GET  /lynchpin/api/meta    -> { code, data: { progress, pass }, msg }
 *   GET  /lynchpin/api/detail  -> { code, data: { story, flow }, msg }
 *   GET  /lynchpin/api/status  -> 需要 token，否则 401 { message, reason: 'NO_TOKEN' }
 *   POST /lynchpin/api/submit  -> body: { qId, aId }，返回 { code, data: { selection, myResult }, msg }
 *
 * 开发时走 vite proxy 透传真实接口；任何失败都会回退到已抓取的本地快照
 * (src/data/lynchpin-api.json)，因此离线、无 token 也能完整跑通全部流程。
 */
import type { ApiEnvelope, AnswerKey, DetailData, MetaData, MyResult, SubmitResult } from '../types';
import snapshot from '../data/lynchpin-api.json';

const API = '/lynchpin/api';

/** 当前数据来源，供调试面板显示 */
export type Source = 'remote' | 'snapshot';
let lastSource: Source = 'snapshot';
export const getLastSource = (): Source => lastSource;

const snapshotMeta = snapshot.meta as ApiEnvelope<MetaData>;
const snapshotDetail = snapshot.detail as ApiEnvelope<DetailData>;

async function getJson<T>(path: string, timeoutMs = 8000): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${API}${path}`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal: ctl.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 本地跑通开关：当上游 /meta 没有 pass 字段时（即未登录，线上会永远停在加载页），
 * 用抓取快照里的 pass 补上，让本地可以看到完整答题流程。
 *
 * 说明：pass 是登录后才会出现的字段。抓取时页面已登录，快照里因此有 pass:true。
 * 若把 pass 缺失如实视为"未登录"，本地打开只会停在加载页 —— 那对研究代码没有帮助。
 * 置为 false 可严格复刻线上未登录行为。
 */
export const ALLOW_LOCAL_PLAYTHROUGH = true;

export async function fetchMeta(): Promise<ApiEnvelope<MetaData>> {
  try {
    const json = await getJson<ApiEnvelope<MetaData>>('/meta');
    if (typeof json?.data?.progress !== 'number') throw new Error('bad payload');
    lastSource = 'remote';
    // 未登录（无 pass）：本地用快照补上，见 ALLOW_LOCAL_PLAYTHROUGH
    if (ALLOW_LOCAL_PLAYTHROUGH && json.data.pass === undefined) {
      return { ...json, data: { ...json.data, pass: true } };
    }
    return json;
  } catch {
    lastSource = 'snapshot';
    return snapshotMeta;
  }
}

export async function fetchDetail(): Promise<ApiEnvelope<DetailData>> {
  try {
    const json = await getJson<ApiEnvelope<DetailData>>('/detail');
    if (!json?.data?.story?.questions || !Array.isArray(json.data.flow)) throw new Error('bad payload');
    lastSource = 'remote';
    return json;
  } catch {
    lastSource = 'snapshot';
    return snapshotDetail;
  }
}

/** /status 的响应体（原站把它整个合进 store） */
export interface StatusData {
  selection?: Record<string, AnswerKey>;
  myResult?: MyResult | null;
  visitedGuides?: string[];
  reason?: string;
}

/**
 * GET /status —— 原站语义：
 *   code 0  -> 合并 selection，ready:true，end = ('myResult' in data)
 *   code -1 且 reason === 'NO_ROLE' -> reset()
 *
 * 离线回退：用抓取时页面上的真实状态（13 题作答 + rank 35629）作为返回，
 * 这样本地打开就能直接看到与线上一致的结果盘面。
 */
export async function fetchStatus(): Promise<ApiEnvelope<StatusData>> {
  try {
    const json = await getJson<ApiEnvelope<StatusData>>('/status');
    // NO_ROLE 等业务错误按原样返回给调用方处理
    if (json.code !== 0) return json;
    lastSource = 'remote';
    return json;
  } catch {
    lastSource = 'snapshot';
    const sel = (snapshot.pageState as { selection?: Record<string, AnswerKey> } | undefined)?.selection;
    const rank = (snapshot.pageState as { rank?: number } | undefined)?.rank ?? 35629;
    return {
      code: 0,
      data: {
        selection: sel ?? {},
        myResult: { value: false, rank },
        visitedGuides: [],
      },
      msg: '',
    };
  }
}

/**
 * 提交一次作答。
 * 远端不可用时在本地推演：维护 selection，并按已答"同意"数量合成 rank，
 * 保证离线状态下流程与结果盘面依然完整可用。
 */
export async function submitQuest(
  qId: string,
  aId: AnswerKey,
  local: { selection: Record<string, AnswerKey>; total: number },
): Promise<ApiEnvelope<SubmitResult>> {
  try {
    const res = await fetch(`${API}/submit`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ qId, aId }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as ApiEnvelope<SubmitResult>;
    if (json.code !== 0) throw new Error(json.msg || 'submit failed');
    lastSource = 'remote';
    return json;
  } catch {
    lastSource = 'snapshot';
    const selection = { ...local.selection, [qId]: aId };
    const agree = Object.values(selection).filter((v) => v === 't').length;
    // 离线合成：与远端同为 5 位量级的排名，稳定可复现
    const rank = (agree * 2749 + local.total * 137 + 1063) % 100000;
    return { code: 0, data: { selection, myResult: { value: agree % 2 === 1, rank } }, msg: '' };
  }
}
